// Relative URL — Kratos public is path-split onto our hostname by HTTPRoute.
//
// KNOWN LIMITATION (tracked, intentionally out of this PR's scope):
//   None of the fetch calls here forward an `Accept-Language` header derived
//   from our in-app locale. Kratos honours whatever the browser sends, which
//   is the OS locale — not what the user picked in <LanguageSwitcher/>. The
//   AJAX gap is cheap to close (~5 LOC) but only fixes Kratos's `m.text`
//   fallbacks, which our kratos-i18n catalog already overrides by `m.id`.
//   The real user-visible breakage is form submits (recovery e-mails arriving
//   in the OS locale instead of the picked locale) — those bypass this file
//   entirely (browser POSTs straight to Kratos). Proper fixes are Kratos-side:
//   either configure Kratos to read a `?locale=` query, or thread the locale
//   into form `action` URLs. Until then, document the gap rather than ship a
//   half-solution that masks the bigger form-submit problem.
import type { LoginFlow, RecoveryFlow, Session, SettingsFlow } from '@ory/client'

// `code` is the i18n discriminant — consumers translate it via the
// `kratos.client.error.${code}` catalog key without leaking English upstream
// strings into the UI. `message` is kept for console.error / Sentry.
export type ClientErrorCode = 'network' | 'upstream' | 'parse'

// `Extra` lets settings inject `{ kind: 'reauth_required' }` without baking
// that shape into the login/recovery contracts.
type FetchFlowResult<T, Extra = never> =
  | { kind: 'ok'; flow: T }
  | { kind: 'expired' }
  | { kind: 'error'; status: number; code: ClientErrorCode; message: string }
  | Extra

interface FlowEndpoint {
  path: string
  label: string
}

// Discriminated, not `Extra | null` — an `Extra` containing `null` can't
// collide with the fall-through sentinel.
type StatusOverride<Extra> = (status: number) => { match: true; value: Extra } | { match: false }

const fetchKratosFlow = async <T, Extra = never>(
  endpoint: FlowEndpoint,
  flowId: string,
  signal: AbortSignal | undefined,
  statusOverride?: StatusOverride<Extra>,
): Promise<FetchFlowResult<T, Extra>> => {
  let response: Response
  try {
    response = await fetch(`${endpoint.path}?id=${encodeURIComponent(flowId)}`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
      signal,
    })
  } catch (err) {
    return {
      kind: 'error',
      status: 0,
      code: 'network',
      message: err instanceof Error ? err.message : 'network error',
    }
  }
  if (statusOverride) {
    const override = statusOverride(response.status)
    if (override.match) {
      return override.value
    }
  }
  // 401 (invalid/missing session cookie), 403 (insufficient privilege),
  // 404 / 410 (flow gone) — all indistinguishable from "expired" at the UI
  // layer for login/recovery. Settings overrides 401/403 above for re-auth.
  if ([401, 403, 404, 410].includes(response.status)) {
    return { kind: 'expired' }
  }
  if (!response.ok) {
    return {
      kind: 'error',
      status: response.status,
      code: 'upstream',
      message: `Kratos ${endpoint.label} lookup failed: ${response.status}`,
    }
  }
  try {
    const flow = (await response.json()) as T
    return { kind: 'ok', flow }
  } catch (err) {
    return {
      kind: 'error',
      status: response.status,
      code: 'parse',
      message: err instanceof Error ? err.message : 'invalid JSON from Kratos',
    }
  }
}

export type FetchLoginFlowResult = FetchFlowResult<LoginFlow>

export const fetchLoginFlow = (
  flowId: string,
  signal?: AbortSignal,
): Promise<FetchLoginFlowResult> =>
  fetchKratosFlow<LoginFlow>(
    { path: '/self-service/login/flows', label: 'login flow' },
    flowId,
    signal,
  )

export type FetchRecoveryFlowResult = FetchFlowResult<RecoveryFlow>

export const fetchRecoveryFlow = (
  flowId: string,
  signal?: AbortSignal,
): Promise<FetchRecoveryFlowResult> =>
  fetchKratosFlow<RecoveryFlow>(
    { path: '/self-service/recovery/flows', label: 'recovery flow' },
    flowId,
    signal,
  )

// 401/403 on settings = privileged_session_max_age exceeded → re-auth, not
// a fresh settings flow. Carve-out runs before the default expired mapping.
export type FetchSettingsFlowResult = FetchFlowResult<SettingsFlow, { kind: 'reauth_required' }>

export const fetchSettingsFlow = (
  flowId: string,
  signal?: AbortSignal,
): Promise<FetchSettingsFlowResult> =>
  fetchKratosFlow<SettingsFlow, { kind: 'reauth_required' }>(
    { path: '/self-service/settings/flows', label: 'settings flow' },
    flowId,
    signal,
    (status) =>
      status === 401 || status === 403
        ? { match: true, value: { kind: 'reauth_required' } }
        : { match: false },
  )

interface ContinueWithSettings {
  action?: string
  flow?: { id?: string }
}

// Both shapes Kratos uses to say "carry on in this settings flow".
const settingsFlowIn = (body: unknown): string | null => {
  const payload = body as {
    redirect_browser_to?: string
    continue_with?: ContinueWithSettings[]
  }
  const handoff = payload.continue_with?.find((entry) => entry.action === 'show_settings_ui')
  if (handoff?.flow?.id) {
    return handoff.flow.id
  }
  if (!payload.redirect_browser_to) {
    return null
  }
  try {
    return new URL(payload.redirect_browser_to, window.location.origin).searchParams.get('flow')
  } catch {
    return null
  }
}

export type SubmitRecoveryCodeResult =
  | { kind: 'ok'; settingsFlowId: string }
  | { kind: 'rejected'; flow: RecoveryFlow }
  | { kind: 'dead' }
  | { kind: 'error'; status: number; code: ClientErrorCode; message: string }

// Redeems an admin-issued recovery code. Kratos answers a *successful* redeem
// with 422 `browser_location_change_required` — the settings flow it wants the
// browser to continue in is only reachable through `redirect_browser_to`.
// A spent flow, or one killed by too many wrong codes, 303s to a brand new
// flow instead; `redirect: 'manual'` surfaces that as an opaque response
// rather than silently following it and reporting a stranger's flow as ours.
export const submitRecoveryCode = async (
  flowId: string,
  code: string,
): Promise<SubmitRecoveryCodeResult> => {
  let response: Response
  try {
    response = await fetch(`/self-service/recovery?flow=${encodeURIComponent(flowId)}`, {
      method: 'POST',
      credentials: 'include',
      redirect: 'manual',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify({ method: 'code', code }),
    })
  } catch (err) {
    return {
      kind: 'error',
      status: 0,
      code: 'network',
      message: err instanceof Error ? err.message : 'network error',
    }
  }

  if (response.type === 'opaqueredirect' || response.status === 303) {
    return { kind: 'dead' }
  }
  if (response.status === 410 || response.status === 404) {
    return { kind: 'dead' }
  }

  let body: unknown
  try {
    body = await response.json()
  } catch (err) {
    return {
      kind: 'error',
      status: response.status,
      code: 'parse',
      message: err instanceof Error ? err.message : 'invalid JSON from Kratos',
    }
  }

  // Status alone cannot tell success from rejection here: Kratos answers a
  // successful redeem with 422 + `redirect_browser_to` today, but flipping
  // `use_continue_with_transitions` (schema default false, unset in our config)
  // switches it to 200 + `continue_with`, which is exactly the shape a wrong
  // code also uses. Discriminate on the body, so neither setting silently
  // reclassifies a spent code as a rejection.
  const settingsFlowId = settingsFlowIn(body)
  if (settingsFlowId) {
    return { kind: 'ok', settingsFlowId }
  }
  if (response.status === 422) {
    return {
      kind: 'error',
      status: response.status,
      code: 'upstream',
      message: 'Kratos redeem gave no settings flow to continue in',
    }
  }
  // A wrong code comes back as the same flow re-rendered with an error message.
  if (response.status === 200 || response.status === 400) {
    return { kind: 'rejected', flow: body as RecoveryFlow }
  }

  return {
    kind: 'error',
    status: response.status,
    code: 'upstream',
    message: `Kratos recovery redeem failed: ${response.status}`,
  }
}

export type SubmitPasswordResult =
  | { kind: 'ok' }
  | { kind: 'rejected'; flow: SettingsFlow }
  | { kind: 'reauth_required' }
  | { kind: 'error'; status: number; code: ClientErrorCode; message: string }

// Sets the password through the real settings flow, so Kratos applies the
// configured policy (length, identifier similarity, HaveIBeenPwned). Policy
// violations arrive as 400 with the message on the `password` node.
export const submitSettingsPassword = async (
  flowId: string,
  password: string,
  csrfToken: string | null,
): Promise<SubmitPasswordResult> => {
  let response: Response
  try {
    response = await fetch(`/self-service/settings?flow=${encodeURIComponent(flowId)}`, {
      method: 'POST',
      credentials: 'include',
      redirect: 'manual',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify({
        method: 'password',
        password,
        ...(csrfToken ? { csrf_token: csrfToken } : {}),
      }),
    })
  } catch (err) {
    return {
      kind: 'error',
      status: 0,
      code: 'network',
      message: err instanceof Error ? err.message : 'network error',
    }
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'reauth_required' }
  }
  if (response.ok) {
    return { kind: 'ok' }
  }
  if (response.status === 400) {
    try {
      return { kind: 'rejected', flow: (await response.json()) as SettingsFlow }
    } catch (err) {
      return {
        kind: 'error',
        status: response.status,
        code: 'parse',
        message: err instanceof Error ? err.message : 'invalid JSON from Kratos',
      }
    }
  }
  return {
    kind: 'error',
    status: response.status,
    code: 'upstream',
    message: `Kratos password update failed: ${response.status}`,
  }
}

// /self-service/logout/browser only RETURNS the logout URL+token; the browser
// must follow it to actually drop the session. Two-step keeps that contract.
export const startBrowserLogout = async (): Promise<void> => {
  const res = await fetch('/self-service/logout/browser', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`Kratos logout init failed: ${res.status}`)
  }
  const { logout_url: logoutUrl } = (await res.json()) as { logout_url: string }
  window.location.assign(logoutUrl)
}

export type FetchWhoamiResult =
  | { kind: 'authenticated'; session: Session }
  | { kind: 'anonymous' }
  | { kind: 'error'; status: number; code: ClientErrorCode; message: string }

// Pre-flight before starting a login flow: an active session 303s into
// default_browser_return_url which lands back on /login → loop.
export const fetchWhoami = async (signal?: AbortSignal): Promise<FetchWhoamiResult> => {
  let response: Response
  try {
    response = await fetch('/sessions/whoami', {
      credentials: 'include',
      headers: { accept: 'application/json' },
      signal,
    })
  } catch (err) {
    return {
      kind: 'error',
      status: 0,
      code: 'network',
      message: err instanceof Error ? err.message : 'network error',
    }
  }
  if (response.status === 401 || response.status === 403) {
    return { kind: 'anonymous' }
  }
  if (!response.ok) {
    return {
      kind: 'error',
      status: response.status,
      code: 'upstream',
      message: `Kratos whoami failed: ${response.status}`,
    }
  }
  try {
    const session = (await response.json()) as Session
    return { kind: 'authenticated', session }
  } catch (err) {
    return {
      kind: 'error',
      status: response.status,
      code: 'parse',
      message: err instanceof Error ? err.message : 'invalid JSON from Kratos',
    }
  }
}
